import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/ui/icon";
import { setAuthToken } from "@/lib/api";

/**
 * Handles the OAuth redirect from the worker.
 * Auth token is now sent as an HttpOnly cookie by the OAuth callback route.
 * The callback route redirects to:
 *   - /auth/callback?new=1 → /onboarding (new users)
 *   - /auth/callback → /dashboard (existing users)
 *
 * No token extraction or localStorage needed — auth is cookie-based only.
 */
export function OAuthCallbackPage() {
  const navigate = useNavigate();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    // Check for error in query string
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get("error");
    if (errorParam) {
      navigate(`/login?error=${errorParam}`, { replace: true });
      return;
    }

    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const localDevToken = hashParams.get("token");
    if (localDevToken) {
      setAuthToken(localDevToken);
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
    }

    // Check if new user (sent as query param, not fragment)
    const isNewUser = params.get("new") === "1";
    
    // Navigate after a small delay to ensure cookie is set
    setTimeout(() => {
      navigate(isNewUser ? "/onboarding" : "/dashboard", { replace: true });
    }, 50);
  }, [navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3">
        <Icon name="refresh" size={24} className="text-muted-foreground animate-spin" />
        <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
          Signing you in...
        </p>
      </div>
    </div>
  );
}
